/**
 * lib/reports/exportWeeklyReportDoc.ts — Weekly Report → Word (.doc) export
 * ============================================================================
 * No docx-generation dependency is added for this — Word (and Google Docs)
 * both open an HTML document saved with a .doc extension + the right MIME
 * type just fine, so this builds a small self-contained HTML string from the
 * SAME already-narrated text used on-screen/in the PDF (nothing recomputed
 * or re-worded here) and triggers a browser download.
 */

import type { WeeklyReportPayload } from './buildWeeklyReportPayload';

interface ExportSection {
  title: string;
  text: string;
  tone: 'good' | 'bad' | 'neutral' | 'brown';
  impactTrend?: { text: string; tone: 'good' | 'bad' | 'brown' };
}

interface ExportHealthFinding {
  title: string;
  explanation: string;
  action: string;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

const TONE_COLOR: Record<'good' | 'bad' | 'neutral' | 'brown', string> = {
  good: '#0F7B4E',
  bad: '#EF4444',   // bright red
  neutral: '#1A1A1A',
  brown: '#78350F',
};

export function downloadWeeklyReportDoc(
  payload: WeeklyReportPayload,
  sections: ExportSection[],
  health: ExportHealthFinding[],
  generatedAtLabel: string,
): void {
  const sectionsHtml = sections.map(s => `
    <div style="margin-bottom:12px;">
      <div style="font-weight:bold; font-size:11pt; margin-bottom:2px;">${escapeHtml(s.title)}</div>
      <div style="font-size:10.5pt; color:${TONE_COLOR[s.tone]};">${escapeHtml(s.text)}</div>
      ${s.impactTrend
        ? `<div style="font-size:10.5pt; color:${TONE_COLOR[s.impactTrend.tone]}; margin-top:4px;">${escapeHtml(s.impactTrend.text)}</div>`
        : ''}
    </div>`).join('');

  const healthHtml = health.length === 0
    ? '<p style="font-size:10.5pt;">No significant electrical health findings this week.</p>'
    : health.map((h, i) => `
      <div style="margin-bottom:12px; border-left:2px solid #0F5C8A; padding-left:10px;">
        <div style="font-weight:bold; font-size:11pt; margin-bottom:2px;">${i + 1}. ${escapeHtml(h.title)}</div>
        <div style="font-size:10.5pt; margin-bottom:4px;">${escapeHtml(h.explanation)}</div>
        <div style="background:#F2F6FA; border-radius:3px; padding:6px;">
          <div style="font-weight:bold; font-size:9pt; color:#0F5C8A; margin-bottom:2px;">Recommended Action</div>
          <div style="font-size:10pt;">${escapeHtml(h.action)}</div>
        </div>
      </div>`).join('');

  const html = `<!DOCTYPE html>
<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
<head>
  <meta charset="utf-8">
  <title>Weekly Impact Report</title>
</head>
<body style="font-family: Calibri, Arial, sans-serif; color:#1A1A1A;">
  <h1 style="font-size:18pt; margin-bottom:2px;">Weekly Impact Report</h1>
  <p style="font-size:10pt; color:#6B6B6B; margin-top:0;">${escapeHtml(payload.reportWeekLabel)}</p>

  <table style="border:1px solid #D9D9D9; border-collapse:collapse; width:100%; margin-bottom:16px;">
    <tr><td style="padding:4px 8px; color:#6B6B6B; font-size:9pt; width:140px;">Consumer</td><td style="padding:4px 8px; font-weight:bold; font-size:9pt;">${escapeHtml(payload.facility.consumer)}</td></tr>
    <tr><td style="padding:4px 8px; color:#6B6B6B; font-size:9pt;">DISCOM / Tariff</td><td style="padding:4px 8px; font-weight:bold; font-size:9pt;">${escapeHtml(payload.facility.discomTariff)}</td></tr>
    <tr><td style="padding:4px 8px; color:#6B6B6B; font-size:9pt;">Contract Demand</td><td style="padding:4px 8px; font-weight:bold; font-size:9pt;">${payload.facility.contractDemandKva} kVA</td></tr>
    <tr><td style="padding:4px 8px; color:#6B6B6B; font-size:9pt;">Tariff Rates</td><td style="padding:4px 8px; font-weight:bold; font-size:9pt;">${escapeHtml(payload.facility.tariffRatesLabel)}</td></tr>
  </table>

  <h2 style="font-size:13pt; color:#0F5C8A; border-bottom:1px solid #D9D9D9; padding-bottom:3px;">Billing &amp; Utilisation Impact</h2>
  ${sectionsHtml}

  <h2 style="font-size:13pt; color:#0F5C8A; border-bottom:1px solid #D9D9D9; padding-bottom:3px;">Live Electrical Health Monitoring</h2>
  ${healthHtml}

  <p style="font-size:8pt; color:#6B6B6B; border-top:1px solid #D9D9D9; padding-top:6px; margin-top:20px;">
    Generated by Vireon Cortex on ${escapeHtml(generatedAtLabel)} — figures are computed directly from meter telemetry; narration is grounded to those figures only.
  </p>
</body>
</html>`;

  const blob = new Blob(['\ufeff', html], { type: 'application/msword' });
  const url = URL.createObjectURL(blob);
  const safeLabel = payload.reportWeekLabel.replace(/[^\w-]+/g, '_');
  const a = document.createElement('a');
  a.href = url;
  a.download = `Weekly_Impact_Report_${safeLabel}.doc`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
